import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { Card, CardBody, Col, Container, Form, Label, Row } from 'reactstrap'
import * as Yup from 'yup'
import evidenceDiary from '../../../../api/diaries/evidenceDiary/hooks'
import { EvidenceDiary, EvidenceDiaryUpdatePayload } from '../../../../api/diaries/evidenceDiary/types'
import { RouteParams } from '../../../../api/globalTypes'
import { APPLICATION_SETTINGS } from '../../../../common/constants'
import FormActionButtons from '../../../../Components/Common/FormActionButtons'
import Loader from '../../../../Components/Common/Loader'
import PageTitle from '../../../../Components/Common/PageTitle'
import InputNumber from '../../../../Components/FormUI/InputNumber'
import InputText from '../../../../Components/FormUI/InputText'
import { useFormValidationErrorModal } from '../../../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import { calculatePriceM2 } from '../../../../utils/formUtils/fields'
import { denormalizeDecimalFields, normalizeDecimalFields } from '../../../../utils/general'
import { renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../../utils/property'
import style from './EvidenceDiaryEdit.module.css'
import { getEvidenceDiaryValidationSchema } from './formValidation'
import { inputsConfig } from './inputs'

const DECIMAL_FIELDS = ['area', 'price', 'priceM2']

const EvidenceDiaryEdit = () => {
    const { id } = useParams<RouteParams>()
    const history = useHistory()
    const { t } = useTranslation()

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const { data: diary, isPending } = evidenceDiary.GetDiary.useQuery(id)
    const { mutate: updateDiary, isPending: isUpdating } = evidenceDiary.UpdateDiary.useMutation()

    const formik = useFormik<EvidenceDiary>({
        enableReinitialize: true,
        initialValues: (diary ? { ...diary.item, ...normalizeDecimalFields(diary.item, DECIMAL_FIELDS) } : {}) as EvidenceDiary,
        validationSchema: Yup.object(getEvidenceDiaryValidationSchema()),
        onSubmit: (values) => {
            const denormalized = denormalizeDecimalFields(values, DECIMAL_FIELDS) as Record<string, string>

            const request: EvidenceDiaryUpdatePayload = {
                id,
                contractType: values.contractType || null,
                propertyType: values.propertyType || null,
                propertyLocation: values.propertyLocation || null,
                propertyStreet: values.propertyStreet || null,
                evidenceDiarySeller: values.evidenceDiarySeller,
                agencyCommission: values.agencyCommission,
                area: denormalized.area != null ? Number(denormalized.area) : null,
                price: denormalized.price != null ? Number(denormalized.price) : null,
                priceM2: denormalized.priceM2 != null ? Number(denormalized.priceM2) : null,
            }

            updateDiary(request)
        },
    })

    useFormValidationErrorModal(formik, [inputsConfig()])

    if (isPending) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loader />
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Form onSubmit={formik.handleSubmit}>
                    <PageTitle title={t('app.common.evidenceDiary')}>
                        <FormActionButtons disabled={isUpdating} hasCancel cancelAction={() => history.push('/evidence-diary')} />
                    </PageTitle>

                    <div className="d-flex gap-3">
                        <Card className="flex-grow-1">
                            <CardBody className={style.cardBody}>
                                <Label className={style.sectionLabel}>{t('form.diary.propertyInfo')}</Label>
                                <Row className="gy-3 mb-4">
                                    <Col md={4}>
                                        <InputText label={t('table.header.diary.contractType')} id="contractType" formik={formik} />
                                    </Col>
                                    <Col md={4}>
                                        <InputText label={t('table.header.diary.propertyType')} id="propertyType" formik={formik} isRequired />
                                    </Col>
                                    <Col md={4}>
                                        <InputText label={t('app.common.location')} id="propertyLocation" formik={formik} />
                                    </Col>
                                    <Col md={4}>
                                        <InputText label={t('app.common.street')} id="propertyStreet" formik={formik} />
                                    </Col>
                                    <Col md={4}>
                                        <InputNumber
                                            label={t('app.common.area', { areaUnit: renderAreaUnit(areaUnit) })}
                                            id="area"
                                            formik={formik}
                                            isFormated
                                            isRequired
                                            onValueChange={(value: string) =>
                                                formik.setFieldValue('priceM2', calculatePriceM2(formik, formik.values.price, value))
                                            }
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <InputNumber
                                            label={t('table.header.diary.price', { price: renderCurrency(currency) })}
                                            id="price"
                                            formik={formik}
                                            isFormated
                                            isRequired
                                            onValueChange={(value: string) =>
                                                formik.setFieldValue('priceM2', calculatePriceM2(formik, value, formik.values.area))
                                            }
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <InputNumber
                                            label={t('table.header.diary.priceM2', { priceM2: renderCurrencyWithUnit(currency, areaUnit) })}
                                            id="priceM2"
                                            formik={formik}
                                            isFormated
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <InputText label={t('app.common.agencyCommission')} id="agencyCommission" formik={formik} isFormated />
                                    </Col>
                                </Row>

                                <Label className={style.sectionLabel}>{t('form.diary.sellerInfo')}</Label>
                                {(formik.values?.evidenceDiarySeller ?? []).map((_, index) => (
                                    <Row key={index} className="gy-3 mb-3">
                                        <Col md={5}>
                                            <InputText
                                                label={t('form.diary.fullName')}
                                                id={`evidenceDiarySeller[${index}].fullName`}
                                                formik={formik}
                                                isRequired
                                            />
                                        </Col>
                                        <Col md={5}>
                                            <InputNumber
                                                label={t('app.common.pin')}
                                                id={`evidenceDiarySeller[${index}].taxNumber`}
                                                formik={formik}
                                                maxLength={11}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                            </CardBody>
                        </Card>
                    </div>

                    <div className="d-flex justify-content-end mt-3 mb-4">
                        <FormActionButtons disabled={isUpdating} hasCancel cancelAction={() => history.push('/evidence-diary')} />
                    </div>
                </Form>
            </Container>
        </div>
    )
}

export default EvidenceDiaryEdit
